import { Component, Input, forwardRef, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../services/global-environment';
import { Subject } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

interface ApiData {
  [key: string]: any;
}

interface SelectedItem {
  item: ApiData;
  index: number;
}

interface ApiResponse {
  success: boolean;
  data: ApiData[];
}

@Component({
  selector: 'app-form-dynamic-api-table',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="form-section">
      <div class="form-header">
        <label [class.required-field]="required">{{ label }}</label>
        <button
          type="button"
          class="collapse-toggle"
          (click)="toggleCollapse()"
          *ngIf="!loading && !hasError && tableData.length > 0"
        >
          <i class="fas" [class.fa-chevron-up]="!isCollapsed" [class.fa-chevron-down]="isCollapsed"></i>
        </button>
      </div>

      <!-- 選擇計數顯示 - 一直顯示 -->
      <div *ngIf="!loading && !hasError && tableData.length > 0" class="selection-info">
        <span class="selection-count">
          已選擇 {{ selectedItems.length }} / {{ tableData.length }} 項
          <span *ngIf="maxSelections" class="max-limit">
            (最多可選 {{ maxSelections }} 項)
          </span>
        </span>
      </div>

      <!-- 可收合的內容區域 -->
      <div class="collapsible-content" [class.collapsed]="isCollapsed">
        <!-- 載入狀態 -->
        <div *ngIf="loading" class="loading-container">
          <div class="loading-spinner">
            <i class="fas fa-spinner fa-spin"></i>
            載入中...
          </div>
        </div>

        <!-- 錯誤狀態 -->
        <div *ngIf="hasError && !loading" class="error-container">
          <div class="error-message">
            <i class="fas fa-exclamation-triangle"></i>
            載入資料失敗，請稍後再試
          </div>
          <button class="btn btn-sm btn-primary" (click)="reloadData()">
            <i class="fas fa-redo"></i> 重新載入
          </button>
        </div>

        <!-- 空資料狀態 -->
        <div *ngIf="!loading && !hasError && tableData.length === 0" class="empty-state">
          <i class="fas fa-inbox"></i>
          <p>暫無資料</p>
        </div>

        <!-- 表格內容 -->
        <div *ngIf="!loading && !hasError && tableData.length > 0" class="table-container">
          <div class="table-wrapper">
            <table class="data-table">
              <thead>
                <tr>
                  <th class="select-column">
                    <input
                      type="checkbox"
                      [checked]="isAllSelected()"
                      [indeterminate]="isPartiallySelected()"
                      (change)="toggleSelectAll($event)"
                      [disabled]="maxSelections && maxSelections === selectedItems.length && !isAllSelected()"
                    >
                    <span class="select-all-label">全選</span>
                  </th>
                  <th *ngFor="let column of tableColumns" [class]="'column-' + column">
                    {{ getColumnDisplayName(column) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let item of tableData; let i = index"
                    [class.selected-row]="isItemSelected(item, i)"
                    [class.clickable-row]="!isRowDisabled(item, i)"
                    (click)="onRowClick(item, i, $event)">
                  <td class="select-column" (click)="$event.stopPropagation()">
                    <input
                      type="checkbox"
                      [checked]="isItemSelected(item, i)"
                      (change)="toggleItemSelection(item, i, $event)"
                      [disabled]="maxSelections && maxSelections === selectedItems.length && !isItemSelected(item, i)"
                    >
                  </td>
                  <td *ngFor="let column of tableColumns" [class]="'column-' + column">
                    {{ getDisplayValue(item[column]) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormDynamicApiTableComponent),
      multi: true
    }
  ],
  styleUrls: ['total-component.scss']
})
export class FormDynamicApiTableComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges {
  @Input() label: string = '';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() placeholder: string = '';
  @Input() required: boolean = false;
  @Input() apiKey: string = '';
  @Input() maxSelections?: number;

  selectedItems: SelectedItem[] = [];
  tableData: ApiData[] = [];
  tableColumns: string[] = [];
  loading: boolean = false;
  hasError: boolean = false;
  isCollapsed: boolean = false;

  private destroy$ = new Subject<void>();
  private apiUrl = environment.apiUrl;
  private cache: Map<string, ApiData[]> = new Map();

  // ControlValueAccessor callback 函數
  onChange = (value: any) => {};
  onTouched = () => {};

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    if (this.apiKey) {
      this.loadData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // 當 apiKey 變更時，重新載入資料
    if (changes['apiKey'] && !changes['apiKey'].firstChange && changes['apiKey'].currentValue) {
      this.loadData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadData(): void {
    if (!this.apiKey) {
      console.warn('FormDynamicApiTableComponent: apiKey is required');
      return;
    }

    // 檢查快取 - 使用 companyid + apiKey 作為快取鍵
    const companyid = environment.companyid;
    const cacheKey = `${companyid}_${this.apiKey}`;

    if (this.cache.has(cacheKey)) {
      this.tableData = this.cache.get(cacheKey) || [];
      this.setupTableColumns();
      return;
    }

    if (!companyid) {
      console.error('FormDynamicApiTableComponent: companyid is not available in environment');
      this.hasError = true;
      return;
    }

    this.loading = true;
    this.hasError = false;
    this.tableData = [];
    this.tableColumns = [];

    const url = `${this.apiUrl}/api/dynamic-data/proxy`;
    const requestBody = {
      companyid: companyid,
      key: this.apiKey
    };

    this.http.post<ApiResponse>(url, requestBody)
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('Failed to load dynamic data:', error);
          this.hasError = true;
          return of({ success: false, data: [] });
        })
      )
      .subscribe(response => {
        this.loading = false;

        if (response.success && Array.isArray(response.data)) {
          this.tableData = response.data;
          this.setupTableColumns();
          // 快取結果 - 使用 companyid + apiKey 作為快取鍵
          this.cache.set(cacheKey, this.tableData);
        } else {
          this.hasError = true;
          console.error('Invalid API response format:', response);
        }
      });
  }

  private setupTableColumns(): void {
    if (this.tableData.length > 0) {
      // 從第一筆資料取得所有欄位名稱
      this.tableColumns = Object.keys(this.tableData[0]);
    }
  }

  // 全選/反選功能
  isAllSelected(): boolean {
    return this.tableData.length > 0 && this.selectedItems.length === this.tableData.length;
  }

  isPartiallySelected(): boolean {
    return this.selectedItems.length > 0 && this.selectedItems.length < this.tableData.length;
  }

  toggleSelectAll(event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;

    if (isChecked) {
      // 全選 - 考慮最大選取限制
      const itemsToSelect = this.maxSelections ?
        this.tableData.slice(0, this.maxSelections) :
        this.tableData;

      this.selectedItems = itemsToSelect.map((item, index) => ({ item, index }));
    } else {
      // 全不選
      this.selectedItems = [];
    }

    this.onSelectionChange();
  }

  // 列點擊功能
  onRowClick(item: ApiData, index: number, event: Event): void {
    // 如果該項目已被禁用，則不執行任何操作
    if (this.isRowDisabled(item, index)) {
      return;
    }

    // 切換選中狀態
    const isCurrentlySelected = this.isItemSelected(item, index);

    if (!isCurrentlySelected) {
      // 要選中該項目，檢查是否超過最大選擇限制
      if (!this.maxSelections || this.selectedItems.length < this.maxSelections) {
        this.selectedItems = [...this.selectedItems, { item, index }];
      }
    } else {
      // 要取消選中該項目
      this.selectedItems = this.selectedItems.filter(selected => selected.index !== index);
    }

    this.onSelectionChange();
  }

  // 檢查列是否被禁用
  isRowDisabled(item: ApiData, index: number): boolean {
    // 如果已達到最大選擇限制且該項目未被選中，則禁用
    return this.maxSelections !== undefined &&
           this.maxSelections === this.selectedItems.length &&
           !this.isItemSelected(item, index);
  }

  // 單項選擇功能
  isItemSelected(item: ApiData, index: number): boolean {
    return this.selectedItems.some(selected => selected.index === index);
  }

  toggleItemSelection(item: ApiData, index: number, event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;

    if (isChecked) {
      // 檢查是否超過最大選擇限制
      if (!this.maxSelections || this.selectedItems.length < this.maxSelections) {
        this.selectedItems = [...this.selectedItems, { item, index }];
      }
    } else {
      this.selectedItems = this.selectedItems.filter(selected => selected.index !== index);
    }

    this.onSelectionChange();
  }

  private compareItems(item1: ApiData, item2: ApiData): boolean {
    // 如果有 id 欄位，優先使用 id 比較
    if (item1['id'] && item2['id']) {
      return item1['id'] === item2['id'];
    }
    // 否則使用 JSON 字串比較（簡單但可能不夠精確）
    return JSON.stringify(item1) === JSON.stringify(item2);
  }

  private onSelectionChange(): void {
    // 回傳選中項目的資料（不包含 index）
    const selectedData = this.selectedItems.map(selected => selected.item);
    this.onChange(selectedData);
    this.onTouched();
  }

  // 顯示相關的輔助方法
  getColumnDisplayName(column: string): string {
    // 可以在這裡定義欄位名稱的顯示映射
    const displayNames: { [key: string]: string } = {
      'id': 'ID',
      'name': '名稱',
      'value': '值',
      'label': '標籤',
      'description': '描述',
      'status': '狀態'
    };

    return displayNames[column] || column;
  }

  getDisplayValue(value: any): string {
    if (value === null || value === undefined) {
      return '-';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }

  // ControlValueAccessor 實作
  writeValue(value: any): void {
    if (Array.isArray(value)) {
      // 需要找到對應的索引來重建 selectedItems
      this.selectedItems = [];
      value.forEach(valueItem => {
        const foundIndex = this.tableData.findIndex(tableItem =>
          this.compareItems(tableItem, valueItem)
        );
        if (foundIndex !== -1) {
          this.selectedItems.push({ item: this.tableData[foundIndex], index: foundIndex });
        }
      });
    } else {
      this.selectedItems = [];
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // 可以在這裡處理禁用狀態
  }

  // 收合功能
  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  // 重新載入資料（如果需要的話）
  reloadData(): void {
    const companyid = environment.companyid;
    const cacheKey = `${companyid}_${this.apiKey}`;
    this.cache.delete(cacheKey);
    this.loadData();
  }
}
